import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import JsonLd from '../../components/JsonLd/JsonLd';
import MainNav from '../../components/MainNav/MainNav';
import Footer from '../../components/Footer/Footer';
import AnalyticsTracker from '../../components/Analytics/AnalyticsTracker';
import {
  trackFormSubmission,
  trackPhoneCall,
  trackEmailClick,
} from '../../components/Analytics/Analytics';
import {
  KIT_DIGITAL_OFFICIAL_URL,
  KIT_DIGITAL_CATALOG_URL,
  ACCELERA_PYME_PHONE,
  LANDING_CANONICAL,
  LANDING_CONVOCATORIA_NOTICE,
  LANDING_HIGHLIGHTS,
  LANDING_EASY_SOLUTIONS,
  LANDING_FAQ,
  LANDING_SERVICE_JSON_LD,
  SEGMENTS,
  PROCESS_STEPS,
} from '../../data/kitDigitalContent';
import './KitDigitalLandingPage.css';

const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  url: LANDING_CANONICAL,
  mainEntity: LANDING_FAQ.map((item) => ({
    '@type': 'Question',
    name: item.question,
    acceptedAnswer: {
      '@type': 'Answer',
      text: item.answer,
    },
  })),
};

const BREADCRUMB_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: [
    {
      '@type': 'ListItem',
      position: 1,
      name: 'Inicio',
      item: 'https://e-asy.es/',
    },
    {
      '@type': 'ListItem',
      position: 2,
      name: 'Kit Digital',
      item: LANDING_CANONICAL,
    },
  ],
};

const HUBSPOT_FORM_ID = '07338dd3-a5af-40fc-8817-ad9235fbab0c';
const HUBSPOT_PORTAL_ID = '25730550';

const KitExternalLink = ({ href, children }) => (
  <a href={href} target="_blank" rel="noopener noreferrer" className="kit-ld-external-link">
    {children}
  </a>
);

const KitDigitalLandingPage = ({ darkMode, toggleDarkMode }) => {
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://js-eu1.hsforms.net/forms/embed/25730550.js';
    script.defer = true;
    document.head.appendChild(script);

    const applyHubSpotStyles = () => {
      const container = document.querySelector('.kit-ld-form-card .hubspot-form-container');
      if (!container || !darkMode) return;

      container.querySelectorAll('label, span, div, p, legend, strong').forEach((el) => {
        if (!['INPUT', 'TEXTAREA', 'SELECT', 'BUTTON'].includes(el.tagName)) {
          el.style.setProperty('color', '#fff', 'important');
        }
      });

      container.querySelectorAll('input, textarea, select').forEach((input) => {
        input.style.backgroundColor = '#374151';
        input.style.borderColor = '#4b5563';
        input.style.color = '#fff';
      });

      container.querySelectorAll('button, .hs-button').forEach((button) => {
        button.style.background = 'linear-gradient(135deg, #18191a 0%, #232323 100%)';
        button.style.color = '#fff';
        button.style.border = 'none';
        button.style.borderRadius = '10px';
        button.style.padding = '14px 28px';
        button.style.fontWeight = '600';
        button.style.cursor = 'pointer';
      });
    };

    script.onload = () => {
      setTimeout(applyHubSpotStyles, 1000);
      const interval = setInterval(applyHubSpotStyles, 2000);
      setTimeout(() => clearInterval(interval), 10000);
    };

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, [darkMode]);

  const handleFormSubmit = () => {
    trackFormSubmission('kit_digital_landing_form');
  };

  return (
    <>
      <SEO
        title="Kit Digital 2026 | Bono hasta 12.000 € para pymes y autónomos | E-ASY"
        description="Solicita el Kit Digital con E-ASY, Agente Digitalizador Adherido. Bono de hasta 12.000 € para digitalizar tu pyme o autónomo: ERP, web, CRM y automatización. Canarias y España."
        keywords="kit digital, kit digital 2026, bono kit digital, solicitar kit digital, agente digitalizador, subvención digitalización pymes, kit digital autónomos, ayudas digitalización empresa, kit digital canarias, kit digital tenerife, bono digital pyme"
        canonical={LANDING_CANONICAL}
      />

      <JsonLd id="jsonld-kit-landing-faq" data={FAQ_SCHEMA} />
      <JsonLd id="jsonld-kit-landing-service" data={LANDING_SERVICE_JSON_LD} />
      <JsonLd id="jsonld-kit-landing-breadcrumb" data={BREADCRUMB_SCHEMA} />

      <AnalyticsTracker
        pageTitle="Kit Digital Landing - E-ASY"
        pageLocation="/kit-digital"
        darkMode={darkMode}
      />

      <MainNav darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className={`kit-ld-page ${darkMode ? 'dark' : 'light'}`} id="contenido-principal">
        <section className="kit-ld-hero">
          <div className="kit-ld-hero-inner">
            <div className="kit-ld-hero-copy">
              <p className="kit-ld-eyebrow">Agente Digitalizador Adherido · Red.es</p>
              <h1 className="kit-ld-title">
                Kit Digital: bono de hasta 12.000 € para digitalizar tu empresa
              </h1>
              <p className="kit-ld-lede">
                El <strong>Kit Digital</strong> subvenciona soluciones del catálogo oficial para{' '}
                <strong>pymes, microempresas y autónomos</strong>. En <strong>E-ASY</strong> te
                ayudamos a solicitar el bono, elegir categorías (ERP, web, CRM, procesos…) e
                implantarlas con acompañamiento real.
              </p>
            </div>

            <aside id="kit-ld-form" className="kit-ld-form-card" aria-labelledby="kit-ld-form-heading">
              <h2 id="kit-ld-form-heading" className="kit-ld-form-title">
                Solicita información del Kit Digital
              </h2>
              <p className="kit-ld-form-subtitle">
                Cuéntanos si ya tienes bono, tu sector y qué quieres digitalizar. Respuesta en
                24–48 h laborables.
              </p>
              <div className="hubspot-form-container">
                <div
                  className="hs-form-frame"
                  data-region="eu1"
                  data-form-id={HUBSPOT_FORM_ID}
                  data-portal-id={HUBSPOT_PORTAL_ID}
                  onLoad={handleFormSubmit}
                />
              </div>
              <p className="kit-ld-form-note">
                Sin compromiso. Revisamos encaje con el catálogo y tu segmento.
              </p>
            </aside>

            <div className="kit-ld-hero-meta">
              <ul className="kit-ld-highlights">
                {LANDING_HIGHLIGHTS.map((item) => (
                  <li key={item.label}>
                    <span className="kit-ld-highlight-value">{item.value}</span>
                    <span className="kit-ld-highlight-label">{item.label}</span>
                  </li>
                ))}
              </ul>

              <div className="kit-ld-hero-contact">
                <a
                  href="tel:+34822685012"
                  className="kit-ld-phone-cta"
                  onClick={() => trackPhoneCall('+34 822 68 50 12')}
                >
                  Llamar: +34 822 68 50 12
                </a>
                <a
                  href="mailto:comercial@e-asy.es"
                  className="kit-ld-email-link"
                  onClick={() => trackEmailClick('comercial@e-asy.es')}
                >
                  comercial@e-asy.es
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="kit-ld-notice" aria-label="Estado de la convocatoria">
          <div className="kit-ld-notice-inner">
            <p>{LANDING_CONVOCATORIA_NOTICE}</p>
            <p className="kit-ld-notice-links">
              <KitExternalLink href={KIT_DIGITAL_OFFICIAL_URL}>
                Información oficial (Acelera Pyme)
              </KitExternalLink>
              {' · '}
              <KitExternalLink href={KIT_DIGITAL_CATALOG_URL}>
                Ver E-ASY en el catálogo
              </KitExternalLink>
              {' · '}
              Tel. Acelera Pyme:{' '}
              <a href={`tel:${ACCELERA_PYME_PHONE.replace(/\s/g, '')}`}>
                {ACCELERA_PYME_PHONE}
              </a>
            </p>
          </div>
        </section>

        <section className="kit-ld-section">
          <div className="kit-ld-section-inner">
            <h2 className="kit-ld-section-title">Importe del bono según tu empresa</h2>
            <p className="kit-ld-section-lede">
              El techo de ayuda depende del número de empleados. Cada solución del catálogo tiene
              además un importe máximo propio dentro de ese bono.
            </p>
            <div className="kit-ld-segments">
              {SEGMENTS.slice(0, 3).map((seg) => (
                <article key={seg.id} className="kit-ld-segment-card">
                  <p className="kit-ld-segment-label">{seg.label}</p>
                  <p className="kit-ld-segment-bono">{seg.bono}</p>
                  <p className="kit-ld-segment-desc">{seg.employees}</p>
                </article>
              ))}
            </div>
            <p className="kit-ld-segment-footnote">
              Medianas empresas (50–250 empleados) pueden acceder a convocatorias con bonos de
              25.000 € o 29.000 €. Consulta el detalle en la web oficial del programa.
            </p>
          </div>
        </section>

        <section className="kit-ld-section kit-ld-section-alt">
          <div className="kit-ld-section-inner">
            <h2 className="kit-ld-section-title">Soluciones Kit Digital que implantamos en E-ASY</h2>
            <p className="kit-ld-section-lede">
              Nos centramos en proyectos que mejoran la gestión del día a día, no en “marcar casillas”.
            </p>
            <div className="kit-ld-solutions">
              {LANDING_EASY_SOLUTIONS.map((item) => (
                <article key={item.title} className="kit-ld-solution-card">
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                  <Link to={item.link} className="kit-ld-solution-link">
                    Ver servicio
                  </Link>
                </article>
              ))}
            </div>
            <p className="kit-ld-more-link">
              Más detalle en la{' '}
              <Link to="/solicitud-kit-digital">página completa del Kit Digital</Link>.
            </p>
          </div>
        </section>

        <section className="kit-ld-section">
          <div className="kit-ld-section-inner">
            <h2 className="kit-ld-section-title">Cómo funciona el Kit Digital con E-ASY</h2>
            <ol className="kit-ld-steps">
              {PROCESS_STEPS.map((step) => (
                <li key={step.step} className="kit-ld-step">
                  <span className="kit-ld-step-num">{step.step}</span>
                  <div>
                    <h3>{step.title}</h3>
                    <p>{step.description}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="kit-ld-section kit-ld-faq">
          <div className="kit-ld-section-inner">
            <h2 className="kit-ld-section-title">Preguntas frecuentes sobre el Kit Digital</h2>
            {LANDING_FAQ.map((item) => (
              <details key={item.question} className="kit-ld-faq-item">
                <summary>{item.question}</summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="kit-ld-final-cta">
          <div className="kit-ld-final-cta-inner">
            <h2>¿Preparas la solicitud del Kit Digital?</h2>
            <p>
              Rellena el formulario o llámanos. Te decimos qué categorías encajan con tu negocio y
              cómo aprovechar el bono sin sobrepasar los topes de tu segmento.
            </p>
            <div className="kit-ld-final-cta-actions">
              <a href="#kit-ld-form" className="kit-ld-btn kit-ld-btn-primary">
                Solicitar información
              </a>
              <a href="tel:+34822685012" className="kit-ld-btn kit-ld-btn-secondary">
                +34 822 68 50 12
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer darkMode={darkMode} />
    </>
  );
};

export default KitDigitalLandingPage;
